// Clear storage and caches for Force Update workflow.
// Does NOT unregister service workers so the new SW can take over after reload.
// Does NOT delete IndexedDB so offline sync queue can persist for the next session.

import { useAuthStore } from '@/domains/auth/stores/useAuthStore';
import { useRouteStore } from '@/domains/route/stores/useRouteStore';
import { useRouteStopStore } from '@/domains/routeStop/stores/useRouteStopStore';
import { useDeviceImageStore } from '@/domains/deviceImages/stores/useDeviceImageStore';
import { useNotificationStore } from '@/core/notifications/useNotificationStore';

const clearLocalStorage = (): Promise<void> => {
  return new Promise((resolve) => {
    try {
      localStorage.clear();
      console.info('[clearStorageForUpdate] localStorage cleared');
      resolve();
    } catch (error) {
      console.error('[clearStorageForUpdate] Error clearing localStorage:', error);
      resolve();
    }
  });
};

const clearSessionStorage = (): Promise<void> => {
  return new Promise((resolve) => {
    try {
      sessionStorage.clear();
      console.info('[clearStorageForUpdate] sessionStorage cleared');
      resolve();
    } catch (error) {
      console.error('[clearStorageForUpdate] Error clearing sessionStorage:', error);
      resolve();
    }
  });
};

const clearCacheStorage = (): Promise<void> => {
  return new Promise((resolve) => {
    if (!('caches' in globalThis)) {
      resolve();
      return;
    }
    caches
      .keys()
      .then((cacheNames) => Promise.allSettled(cacheNames.map((name) => caches.delete(name))))
      .then(() => {
        console.info('[clearStorageForUpdate] Cache Storage cleared');
        resolve();
      })
      .catch((error) => {
        console.error('[clearStorageForUpdate] Error clearing Cache Storage:', error);
        resolve();
      });
  });
};

const clearCookies = (): Promise<void> => {
  return new Promise((resolve) => {
    try {
      const pastDate = new Date(0).toUTCString();
      const cookiesToClear = ['access_token', 'refresh_token'];
      cookiesToClear.forEach((cookieName) => {
        document.cookie = `${cookieName}=; expires=${pastDate}; path=/`;
        document.cookie = `${cookieName}=; expires=${pastDate}; path=/; domain=${globalThis.location.hostname}`;
        if (globalThis.location.hostname.includes('.azurewebsites.net')) {
          document.cookie = `${cookieName}=; expires=${pastDate}; path=/; domain=.azurewebsites.net`;
        }
      });
      resolve();
    } catch (error) {
      console.error('[clearStorageForUpdate] Error clearing cookies:', error);
      resolve();
    }
  });
};

const resetPiniaStores = (): Promise<void> => {
  return new Promise((resolve) => {
    try {
      const authStore = useAuthStore();
      authStore.actions.reset();
      authStore.actions.setOptInSuccessPayload(null);
      const routeStore = useRouteStore();
      routeStore.clearSelection();
      const routeStopStore = useRouteStopStore();
      routeStopStore.clearFocusedStop();
      const deviceImageStore = useDeviceImageStore();
      deviceImageStore.clearOfflineQueue();
      const notificationStore = useNotificationStore();
      notificationStore.clear();
      resolve();
    } catch (error) {
      console.error('[clearStorageForUpdate] Error resetting Pinia stores:', error);
      resolve();
    }
  });
};

export const clearStorageAndCachesForUpdate = async (): Promise<void> => {
  await Promise.allSettled([
    clearLocalStorage(),
    clearSessionStorage(),
    clearCacheStorage(),
    clearCookies(),
    resetPiniaStores(),
  ]);
};
